/**
 * PropagatingEndpoint - Base class for endpoints that propagate values
 *
 * Provides shared logic for:
 * - Internal endpoint wiring (subgraph exposed->internal)
 * - Target endpoint management
 * - Connection enable/disable
 *
 * Extended by DataEndpoint, HydraEndpoint, ThreeEndpoint, etc.
 */

import type { IEndpoint } from './IEndpoint';
import type { INode } from '@/nodes/INode';
import { DataTypeSpec, DataFlow } from '@/nodes/Endpoint';
import type { Payload } from '@/graph/Payload';
import { logger } from '@/logging';
import type { IRuntimeStore, StoreKey } from './RuntimeStore';
import { EndpointWithContext } from '@/plugins/Plugin';

export abstract class PropagatingEndpoint implements IEndpoint {
  // Identity (from Endpoint)
  abstract readonly id: string;
  abstract readonly dataType: DataTypeSpec;
  abstract readonly dataFlow: DataFlow;
  readonly instancePath: string;
  readonly isInput: boolean;
  readonly isOutput: boolean;

  // Optional config fields
  readonly label?: string;
  readonly primary?: boolean;
  readonly group?: string;
  readonly groupOrder?: number;
  readonly disabled?: boolean;
  readonly transient?: boolean;
  readonly min?: number;
  readonly max?: number;
  readonly step?: number;
  readonly options?: readonly (string | number)[];

  // Node reference for notifications
  node: INode;

  // Connection targets
  targetEndpoints: IEndpoint[] = [];

  // Subgraph mapping: exposed->internal (1-1 relationship)
  internalEndpoint: IEndpoint | null = null;

  // Disabled connections (muted without disconnecting)
  protected disabledConnections: Set<IEndpoint> = new Set();

    // Runtime state
  protected store: IRuntimeStore;
  constructor(config: EndpointWithContext, store: IRuntimeStore) {
    this.instancePath = config.instancePath;
    this.node = config.node;
    this.store=store;
    Object.assign(this, config.endpoint)
  }


  /** Key object for store access */
  get storeKey(): StoreKey {
    return {
      instancePath: this.instancePath,
      nodeId: this.node.id,
      endpointId: this.id
    };
  }

  /** Key for :in activity marker */
  get storeKeyIn(): StoreKey {
    return {
      instancePath: this.instancePath,
      nodeId: this.node.id,
      endpointId: `${this.id}:in`
    };
  }

  /** Key for :out activity marker */
  get storeKeyOut(): StoreKey {
    return {
      instancePath: this.instancePath,
      nodeId: this.node.id,
      endpointId: `${this.id}:out`
    };
  }

  /**
   * Notify node based on dataFlow type.
   * @param source - required for non-param flows (only inputs should call notifyNode)
   */
  protected notifyNode(payload: any, source?: IEndpoint): void {
    if (this.dataFlow !== 'param' && this.dataFlow !== 'trigger' && !source) {
      throw new Error(`notifyNode: source required for ${this.dataFlow} on ${this.instancePath}.${this.node.id}.${this.id}`);
    }

    switch (this.dataFlow) {
      case 'param':
        this.node.onParamChanged(payload, this.id, source);
        break;
      case 'internal':
        this.node.onInternalChanged(payload, this.id, source);
        break;
      case 'event':
        this.node.handleEvent(payload, this.id, source);
        break;
      case 'trigger':
        this.node.handleTrigger(payload, this.id, source);
        break;
    }
  }

  /**
   * Notify node that a connection was removed.
   */
  protected notifyDisconnected(source: IEndpoint): void {
    this.node.onDisconnected(this.id, source);
  }

  // ─────────────────────────────────────────────────────────────
  // TARGET/INPUT BEHAVOUR - subclasses must implement
  // ─────────────────────────────────────────────────────────────

  abstract getValue(): Payload;
  abstract setValue(payload: Payload, source?: IEndpoint): void;
  abstract receive(payload: Payload, source: IEndpoint): void;



  // ─────────────────────────────────────────────────────────────
  // SOURCE/OUTPUT BEHAVIOUR - common propagation logic
  // ─────────────────────────────────────────────────────────────
  /**
   * Propagate a payload to downstream targets.
   * All targets (subgraph internal + graph connections) use receive() so getter
   * functions propagate uniformly and :in markers are set consistently.
   * @param source - If provided, skip this endpoint when propagating (to prevent feedback loops)
   */
  protected propagate(payload: Payload, source?: IEndpoint): void {
    // Route to subgraph via internal endpoint
    if (this.internalEndpoint && this.internalEndpoint !== source && !this.disabledConnections.has(this.internalEndpoint)) {
      this.internalEndpoint.receive(payload, this);
    }

    // Route to connections within the same graph
    for (const target of this.targetEndpoints) {
      if (target !== source && !this.disabledConnections.has(target)) {
        target.receive(payload, this);
      }
    }
  }

  connectTo(target: IEndpoint): void {
    if (this.targetEndpoints.includes(target)) {
      throw new Error('Endpoint alread connected:' + target.id)
    }

    logger.trace('connecting', this.dataType, this.dataFlow, this.internalEndpoint?.id)

    this.targetEndpoints.push(target);

    // For params and internal, the endpoint values should be pushed on connection.
    // Skip during batch mode — values will be distributed after batch ends.
    if ((this.dataFlow === 'param' || this.dataFlow === 'internal') && !this.store.isBatching()) {
      const currentValue = this.getValue();
      if (currentValue === undefined) {
        throw new Error('No value for endpoint ' + this.id)
      }
      target.receive(currentValue, this);
    }

  }

  onConnectedFrom(_source: IEndpoint): void {
    // Default: no-op. Override in subclasses that need connection notifications.
  }

  connectInternal(internal: IEndpoint): void {
    if (this.internalEndpoint) {
      throw new Error('Internal endpoint already connected: ' + this.internalEndpoint.id)
    }
    this.internalEndpoint = internal;
    logger.trace('PropagatingEndpoint', `connectInternal: ${this.storeKey} -> ${internal.storeKey}`);
  }


  disconnectFrom(target: IEndpoint): void {
    const idx = this.targetEndpoints.indexOf(target);
    if (idx !== -1) {
      this.targetEndpoints.splice(idx, 1);
      // Notify target that source was disconnected (clears propagated get)
      if (typeof (target as any).onSourceDisconnected === 'function') {
        (target as any).onSourceDisconnected(this);
      }
    }
    this.disabledConnections.delete(target);
  }

  setConnectionDisabled(target: IEndpoint, disabled: boolean): void {
    if (disabled) {
      this.disabledConnections.add(target);
    } else {
      this.disabledConnections.delete(target);
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Lifecycle
  // ─────────────────────────────────────────────────────────────


  dispose(): void {
    this.targetEndpoints = [];
    this.internalEndpoint = null;
    this.disabledConnections.clear();
  }
}
